/*
 * =====================================================================
 * 작 업 명  :
 * 파 일 명  : drive.js
 * 변경이력
 * 2023-01-05 / 이성준 / 최초작성
 * =====================================================================
 */
import {$session} from "dot-framework"
import DTMSCanvas from "@/util/DTMSCanvas"
import {DotPadInfo} from "@/util/dotpad/DotPadInfo.js"
import driveApi from "@/api/apps/driveApi"

// card형 목록의 DTMS 썸네일 생성을 위한 캔버스 배열
const canvas = [];
// 카드 썸네일은 보이는 크기(카드 260×173 의 테두리 안)로 그린다. 요소를 아직 못 재면 이 크기다(DCW-1946).
// 예전에는 99×99 에 그려 CSS 가 늘려서 핀이 뭉개졌다.
const CARD_THUMBNAIL_WIDTH = 258;
const CARD_THUMBNAIL_HEIGHT = 171;
/**
 * @type {Readonly<{CUT: symbol, COPY: symbol}>}
 */
const CLIPBOARD_MODE = Object.freeze({
    CUT: Symbol("CUT"),
    COPY: Symbol("COPY"),
});
/**
 * @type {Readonly<{CARD: symbol, LIST: symbol}>}
 */
const VIEW_MODE = Object.freeze({
    CARD: Symbol("CARD"),
    LIST: Symbol("LIST"),
});

const dtmsDataFormatter = (data) => {
    return (data && data.items)
        ? data.items.map(item => ({
            ...item,
            DTMS_JSON: item.DTMS_JSON
                ? {
                    ...item.DTMS_JSON,
                    // 판 크기는 파일(항목)의 DEVICE_KIND 로 정한다. 쪽에는 DEVICE_KIND 가 없어 예전에는 늘 320 이 됐고,
                    // 목록 보기에서 목록을 다시 받으면 768 썸네일을 320 배치로 풀어 흩어진 점이 됐다(QA 10/25 NEW-G1-5).
                    items: item.DTMS_JSON.items?.map(subItem => ({
                        ...subItem,
                        dotPadInfo: new DotPadInfo(item.DEVICE_KIND)
                    }))
                }
                : item.DTMS_JSON // DTMS_JSON이 없으면 원래 값 유지
        }))
        : null
}

const state = () => ({
    clipboard: [], // 선택한 행 복사(클립보드)
    clipboardMode: null,
    currentGroup: "",
    driverKind: "P", // Private Drive
    items: [],
    redoStack: [],
    selectedItems: [],
    undoStack: [],
    viewMode: VIEW_MODE.CARD,
    currentPage: 1,
    totalPage: 1,
});

// getters
const getters = {
    clipboard: (state) => {
        return state.clipboard;
    },
    clipboardMode: (state) => {
        return state.clipboardMode;
    },
    driverKind: (state) => {
        return state.driverKind;
    },
    filteredItems: (state) => (textFilter) => {
        //FIXME: metacharacter(?:^|\\W)
        textFilter = textFilter.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(textFilter, 'i');

        return state.items.filter(item => regex.test(item.FILE_NAME));
    },
    getCurrentGroupNo: (state) => {
        return state.currentGroup;
    },
    isPrivate: (state) => {
        return state.driverKind === "P";
    },
    isPublic: (state) => {
        return state.driverKind === "D";
    },
    items: (state) => {
        return state.items;
    },
    selectedItems: (state) => {
        return state.selectedItems;
    },
    viewMode: (state) => {
        return state.viewMode;
    },
    currentPage: (state) => {
        return state.currentPage;
    },
    totalPage: (state) => {
        return state.totalPage;
    }
};

const findIndexOfSelectedItem = (state, item) => {
    return state.selectedItems.findIndex(x => x.FILE_KEY === item.FILE_KEY);
};

// mutations
const mutations = {
    clearClipboard(state) {
        state.clipboard = [];
    },
    clearSelectedItems(state) {
        state.selectedItems = []
    },
    pushToClipboard(state, clipboard) {
        state.clipboard.push(...clipboard)
    },
    pushToSelectedItems(state, data) {
        const items = Array.isArray(data) ? data : [data];

        for (const item of items) {
            const indexToUpdate = findIndexOfSelectedItem(state, item);

            if (indexToUpdate !== -1) {
                state.selectedItems[indexToUpdate] = item;
            } else {
                state.selectedItems.push(item);
            }
        }
    },
    removeSelectedItem(state, item) {
        const indexToRemove = findIndexOfSelectedItem(state, item);

        if (indexToRemove !== -1) {
            state.selectedItems.splice(indexToRemove, 1);
        }
    },
    redo(state) {
        if (state.redoStack.length === 0) return;
        const command = state.redoStack.pop();
        command.redo();
        state.undoStack.push(command);
    },
    setClipboardMode(state, mode) {
        state.clipboardMode = CLIPBOARD_MODE[mode];
    },
    setCurrentGroupNo(state, groupNo) {
        state.currentGroup = groupNo;
    },
    setDriveKindStatus(state, status) {
        state.driverKind = status;
    },
    setItems(state, items) {
        state.items = items;
        // 선택해 둔 항목에 새 목록의 같은 항목(FILE_KEY) 값을 옮겨 적는다(QA 10/24 NEW-G5-1).
        // 선택은 옛 목록의 객체를 그대로 들고 있어서, 이름을 바꾼 뒤 '이름 바꾸기'를 다시 열면 바꾸기 전 이름이
        // 채워졌고(그대로 저장하면 이름이 되돌아갔다) '열기'도 옛 이름을 주소에 실었다.
        // 객체를 바꿔 끼우지 않고 글자·숫자 값만 옮긴다. 발표 모드는 선택한 객체에 전체 쪽 DTMS_JSON·presentationSource 를
        // 얹어 쓰는데(DotCloud.onPresentation), 발표 중에 목록이 다시 오면(변환 중인 항목이 있으면 10초마다) 첫 쪽만 든
        // 목록 객체로 바뀌어 발표가 깨진다.
        // 새 목록에 없는 항목은 그대로 둔다. 목록을 비웠다가 다시 받는 사이(fetchDtmsGroupList)에 선택이 사라지면 안 된다.
        if (!Array.isArray(items) || items.length === 0 || state.selectedItems.length === 0) return;
        const fresh = new Map(items.map(item => [item.FILE_KEY, item]));
        for (const selected of state.selectedItems) {
            const item = fresh.get(selected?.FILE_KEY);
            if (!item || item === selected) continue;
            for (const [key, value] of Object.entries(item)) {
                if (value === null || typeof value !== "object") selected[key] = value;
            }
        }
    },
    setViewMode(state, status) {
        state.viewMode = VIEW_MODE[status];
    },
    undo(state) {
        if (state.undoStack.length === 0) return;
        const command = state.undoStack.pop();
        command.undo();
        state.redoStack.push(command);
    },
    pushUndoStack(state, command) {
        state.undoStack.push(command);
        // 새 커맨드를 실행할 때마다 redo 스택 초기화
        state.redoStack = [];
    },
    setCurrentPage(state, command) {
        state.currentPage = command;
    },
    setTotalPage(state, command) {
        state.totalPage = command;
    }
};

// actions
const actions = {
    /**
     * [Dot Cloud]
     * 메뉴에서 Card형 목록을 볼 때, Card 안에 DTMS 썸네일 이미지를 생성
     * @param context
     */
    createDTMSCanvasOfCardTypeItems(context) {
        for (let i = 0; i < context.state.items.length; i++){
            const obj = context.state.items[i]
            const DEVICE_KIND = context.state.items[i].DEVICE_KIND + ""
            if (obj.FILE_TYPE === 'FILE') {
                const DTMSJson = obj.DTMS_JSON

                if (DTMSJson && Array.isArray(DTMSJson.items) && DTMSJson.items.length > 0) {
                    const dotPadInfo = new DotPadInfo(DEVICE_KIND)
                    DTMSJson.items[0].dotPadInfo = dotPadInfo
                    const pixelArr = DTMSCanvas.getPixelArray(DTMSJson.items[0])
                    const params = {
                        pixelArr: pixelArr,
                        // 색 펜으로 그린 핀은 그 레이어 색으로(DCW-1946)
                        pinColors: DTMSCanvas.getPinColors(DTMSJson.items[0], pixelArr),
                        canvasId: `canvas-${obj.FILE_KEY}-0`,
                        fallbackWidth: CARD_THUMBNAIL_WIDTH,
                        fallbackHeight: CARD_THUMBNAIL_HEIGHT,
                        dotPadInfo
                    }

                    canvas[i] !== undefined && canvas[i].dispose()
                    canvas[i] = DTMSCanvas.drawSharpThumbnail(params)
                }
            }
        }

        canvas.splice(context.state.items.length)
    },
    async executeCommand(context, command) {
        const completed = await command.execute();
        if (completed !== false) context.commit("pushUndoStack", command)
        return completed
    },
    /**
     * @param context
     * @param payload
     * @param {string} payload.parentGroupNo    부모 촉각이미지 그룹번호
     * @param {string|number} payload.pageNo    페이지 번호
     * @param {string|number} payload.pageSize  페이지 사이즈
     * @param {string} payload.appType          A: All, C: Canvas, T: Text Book
     * @param {string} payload.driveKind        D: Drive, P: Private
     */
    async fetchDtmsGroupList(context, payload) {
        const expectedDriverKind = payload?.driveKind || context.state.driverKind;

        context.commit("setItems", []); // 이전 탭 목록 삭제

        let dtmsResponse = await context.dispatch('getDtmsGroupList', payload)
        
        if (dtmsResponse.status === 401) {
            setTimeout(async () => await context.dispatch("reloadItems", payload), 1000)
            return;
        }

        // API 응답이 도착했는데, 그 사이에 사용자가 탭을 바꿨다면?
        // 출발할 때의 탭과 현재 스토어의 탭을 비교
        if (expectedDriverKind !== context.state.driverKind) {
            console.log("[Race Condition 방어] 탭이 변경되어 과거 API 응답을 렌더링하지 않습니다.");
            return;
        }

        // 현재 탭의 정상적인 응답일 경우에만 스토어 업데이트
        context.commit("setCurrentPage", dtmsResponse.pageNo)
        context.commit("setCurrentGroupNo", dtmsResponse.parentGroupNo)
        context.commit("setItems", dtmsResponse.items)
        context.commit("setTotalPage", dtmsResponse.totalPage)
    },
    redo(context) {
        context.commit("redo");
    },
    async getDtmsGroupList(context, payload, rootState, rootGetters) {
        const parentGroupNo = payload?.parentGroupNo || (new URLSearchParams(window.location.search).get('parentGroupNo') || "ROOT")
        const pageNo = payload?.pageNo || 1
        const pageSize = payload?.pageSize || 18
        const appType = payload?.appType || 'A'
        const driverKind = payload?.driveKind || context.state.driverKind
        const params = {
            PAGE_NO: pageNo,
            PAGE_SIZE: pageSize,
            COMP_NO: $session.getCompNo() || rootGetters.COMP_NO,
            PARENT_GROUP_NO: parentGroupNo,
            DRIVER_KIND: driverKind,
            USER_NO: $session.getUserNo() !== "GUEST" ? $session.getUserNo() : "",
            APP_TYPE: appType,
        }

        const response = await driveApi.getDtmsGroupList(params)

        let totalCount = 0
        let totalPage = 0

        if (response.data) {
            totalCount = response.data.TOTAL_COUNT || 0
            totalPage = Math.ceil(totalCount/pageSize)
        }

        return {
            status: response.status,
            items: dtmsDataFormatter(response.data),
            pageNo: pageNo,
            parentGroupNo: parentGroupNo,
            totalPage: totalPage
        }

    },
    async reloadItems(context, payload) {
        const expectedDriverKind = payload?.driveKind || context.state.driverKind;

        await context.dispatch("fetchDtmsGroupList", payload)

        // 탭이 바뀌었다면 화면 랜더링 stop
        if (expectedDriverKind !== context.state.driverKind) return;

        if (context.state.viewMode === VIEW_MODE.CARD) {
            await context.dispatch("createDTMSCanvasOfCardTypeItems")
        }
    },
    undo(context) {
        context.commit("undo")
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
